import { DefaultError, useMutation, UseMutationResult } from '@tanstack/react-query';
import { b2cGraphql } from 'api/b2c-graphql';
import { format } from 'date-fns';
import { EpionDemographicDataSchemaValidated } from 'epion-checkin/schema';

interface EpionCheckInUrlInput {
    firstName: string;
    lastName: string;
    dateOfBirth: string; // YYYY-MM-DD
    emailAddress?: string | null;
    phoneType: 'HOME' | 'MOBILE';
    phone: string;
    sex: 'FEMALE' | 'MALE';
    address: {
        line1: string;
        line2?: string;
        city: string;
        postalCode: string;
        state: string;
    };
}

const query = `#graphql
    mutation GetEpionCheckInUrl($departmentId: Int!, $patientDemographics: PatientDemographicsInput!) {
        kioskCheckin {
            getUrlFromDemographics(departmentId: $departmentId, patientDemographics: $patientDemographics) {
                url
            }
        }
    }
`;

export const useEpionCheckIn = (): UseMutationResult<
    string,
    DefaultError,
    { patientData: EpionDemographicDataSchemaValidated; departmentId: string }
> => {
    return useMutation<string, DefaultError, { patientData: EpionDemographicDataSchemaValidated; departmentId: string }>({
        mutationKey: ['epion-checkin-url'],
        mutationFn: async ({ patientData, departmentId }) => {
            const patientDemographics: EpionCheckInUrlInput = {
                firstName: patientData.firstName,
                lastName: patientData.lastName,
                dateOfBirth: format(new Date(patientData.dateOfBirth), 'yyyy-MM-dd'),
                emailAddress: patientData.email,
                phoneType: patientData.phoneType,
                phone: patientData.phoneNumber,
                sex: patientData.sex,
                address: {
                    line1: patientData.address,
                    line2: patientData.addressCount,
                    city: patientData.city,
                    postalCode: patientData.zip,
                    state: patientData.state,
                },
            };

            const response = await b2cGraphql.post('/graphql', {
                query,
                variables: {
                    departmentId: Number(departmentId),
                    patientDemographics,
                },
            });

            return response.data.data.kioskCheckin.getUrlFromDemographics.url;
        },
    });
};
